import { useMutation } from '@apollo/client';
import React, { useCallback, useState } from 'react';
import Modal, { ModalBody, ModalFooter } from '../../components/Modal';
import { Flex } from 'rebass';
import { Label, Select } from '@rebass/forms';
import Button from '../../components/Button/Button';
import PropTypes from 'prop-types';
import { BillingStatus } from '../../types/assignment';
import useNotify from '../../hooks/useNotify';
import { gql } from '@/modes/company/gql';

type ChangeJobPaidStatusModalProps = {
  onClose: (actionTaken?: boolean) => void;
  assignmentIds: string[] | readonly string[];
};

const SET_BILLING_STATUS = gql(`
  mutation SetJobsPaidStatus(
    $assignmentIds: [ID!]!
    $billingStatus: BillingStatus!
  ) {
    setInboundAssignmentsBillingStatus(
      assignmentIds: $assignmentIds
      billingStatus: $billingStatus
    ) {
      id
      billingStatus
    }
  }
`);

const SetBillingStatusModal: React.FC<ChangeJobPaidStatusModalProps> = ({
  onClose,
  assignmentIds,
}) => {
  const onCancel = useCallback(() => onClose(), []);
  const onActionComplete = useCallback(() => onClose(true), []);
  const { showError } = useNotify();

  const [setBillingStatus, { loading: mutationLoading, error: mutationError }] =
    useMutation(SET_BILLING_STATUS, {
      onCompleted: onActionComplete,
      onError: useCallback((error) => {
        showError(
          `Error Changing Job${
            assignmentIds.length > 1 ? 's' : ''
          } Paid Status`,
          error
        );
      }, []),
    });

  const [billingStatusValue, setBillingStatusValue] = useState<
    BillingStatus | ''
  >('');
  const onBillingStatusChange = useCallback(
    (e: any) => {
      setBillingStatusValue(e.target.value);
    },
    [setBillingStatusValue]
  );

  return (
    <Modal
      title={`Set Job${assignmentIds.length > 1 ? 's' : ''} Billing Status`}
      onClose={onCancel}
    >
      <ModalBody>
        <Flex p={3} flexDirection="column">
          <Label htmlFor="billingStatus">Select Billing Status</Label>
          <Select
            id="billingStatus"
            name="billingStatus"
            value={billingStatusValue}
            onChange={onBillingStatusChange}
            mb={5}
          >
            <option value={''}>- choose status -</option>
            <option value="PAID">Paid</option>
            <option value="NOT_PAID">Not Paid</option>
            <option value="NOT_BILLABLE">Not Billable</option>
          </Select>
          {mutationLoading && <p>Loading...</p>}
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          icon="check-circle"
          disabled={mutationLoading}
          onClick={() => {
            setBillingStatus({
              variables: {
                assignmentIds: assignmentIds as string[],
                billingStatus: billingStatusValue as BillingStatus,
              },
            });
          }}
        >
          Confirm
        </Button>
        <Button variant="outline" icon="slash-circle" onClick={onCancel}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

SetBillingStatusModal.propTypes = {
  onClose: PropTypes.any,
  assignmentIds: PropTypes.any.isRequired,
};

export default SetBillingStatusModal;
